import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import './FinancialTransactions.css';
import { toast } from '../components/Toast';

// ── Date helpers ──────────────────────────────────────────────────────────────
const fmt = (d) => d.toISOString().split('T')[0];

const getPresetRange = (preset) => {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();

  switch (preset) {
    case 'today':      return { start: fmt(today), end: fmt(today) };
    case 'this_week': {
      const day = today.getDay();
      const mon = new Date(today); mon.setDate(today.getDate() - (day === 0 ? 6 : day - 1));
      return { start: fmt(mon), end: fmt(today) };
    }
    case 'this_month':  return { start: fmt(new Date(y, m, 1)), end: fmt(new Date(y, m + 1, 0)) };
    case 'last_month':  return { start: fmt(new Date(y, m - 1, 1)), end: fmt(new Date(y, m, 0)) };
    case 'this_quarter': {
      const q = Math.floor(m / 3);
      return { start: fmt(new Date(y, q * 3, 1)), end: fmt(new Date(y, q * 3 + 3, 0)) };
    }
    case 'this_fy': {
      const fyStart = m >= 3 ? y : y - 1;
      return { start: fmt(new Date(fyStart, 3, 1)), end: fmt(new Date(fyStart + 1, 2, 31)) };
    }
    case 'last_fy': {
      const fyStart = (m >= 3 ? y : y - 1) - 1;
      return { start: fmt(new Date(fyStart, 3, 1)), end: fmt(new Date(fyStart + 1, 2, 31)) };
    }
    default: return null;
  }
};

const PRESETS = [
  { key: 'today',        label: 'Today' },
  { key: 'this_week',    label: 'This Week' },
  { key: 'this_month',   label: 'This Month' },
  { key: 'last_month',   label: 'Last Month' },
  { key: 'this_quarter', label: 'This Quarter' },
  { key: 'this_fy',      label: 'This FY' },
  { key: 'last_fy',      label: 'Last FY' },
];

const TYPE_LABELS = { INCOME: 'Income', EXPENSE: 'Expense', PURCHASE: 'Purchase', PAYMENT: 'Payment', ADVANCE: 'Advance' };
const TYPE_CLASS  = { INCOME: 'badge-income', EXPENSE: 'badge-expense', PURCHASE: 'badge-purchase', PAYMENT: 'badge-payment', ADVANCE: 'badge-advance' };
const MODE_LABELS = { CASH: 'Cash', BANK: 'Bank', UPI: 'UPI', CHEQUE: 'Cheque' };

// ── Edit Modal ────────────────────────────────────────────────────────────────
function EditModal({ txn, onClose, onSaved }) {
  const [form, setForm] = useState({
    transactionType: txn.transactionType,
    transactionDate: txn.transactionDate || '',
    amount:          txn.amount          ? String(txn.amount)        : '',
    taxAmount:       txn.taxAmount       ? String(txn.taxAmount)     : '',
    paymentMode:     txn.paymentMode     || '',
    referenceNo:     txn.referenceNo     || '',
    dueDate:         txn.dueDate         || '',
    notes:           txn.notes           || '',
    customerId:      txn.customerId      ? String(txn.customerId)    : '',
    vendorId:        txn.vendorId        ? String(txn.vendorId)      : '',
    employeeId:      txn.employeeId      ? String(txn.employeeId)    : '',
    payeeName:       txn.payeeName       || '',
    projectId:       txn.projectId       ? String(txn.projectId)     : '',
    accountHeadId:   txn.accountHeadId   ? String(txn.accountHeadId) : '',
    branchId:        txn.branchId        ? String(txn.branchId)      : '',
    payeeType:       txn.vendorId ? 'VENDOR' : txn.employeeId ? 'EMPLOYEE' : txn.payeeName ? 'OTHER' : 'VENDOR',
  });

  const [customers, setCustomers]   = useState([]);
  const [vendors, setVendors]       = useState([]);
  const [employees, setEmployees]   = useState([]);
  const [projects, setProjects]     = useState([]);
  const [accountHeads, setHeads]    = useState([]);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');

  useEffect(() => {
    const type = txn.transactionType;
    if (type === 'INCOME') axios.get('/customers?size=1000').then(r => {
      const d = r.data.data?.content || r.data.content || r.data.data || [];
      setCustomers(Array.isArray(d) ? d : []);
    }).catch(() => {});
    if (type === 'PURCHASE' || type === 'PAYMENT') axios.get('/vendors?size=1000').then(r => {
      const d = r.data.data?.content || r.data.content || r.data.data || [];
      setVendors(Array.isArray(d) ? d : []);
    }).catch(() => {});
    if (type === 'PAYMENT') axios.get('/employees?size=1000').then(r => {
      const d = r.data?.employees || r.data?.data?.employees || r.data?.data || [];
      setEmployees(Array.isArray(d) ? d : []);
    }).catch(() => {});
    axios.get('/projects?size=1000').then(r => {
      const d = r.data.data?.content || r.data.content || r.data.data || [];
      setProjects(Array.isArray(d) ? d : []);
    }).catch(() => {});
    axios.get('/account-heads').then(r => {
      setHeads(Array.isArray(r.data.data || r.data) ? (r.data.data || r.data) : []);
    }).catch(() => {});
  }, [txn.transactionType]);

  const filteredHeads = accountHeads.filter(h => {
    if (txn.transactionType === 'INCOME')   return h.category === 'INCOME';
    if (txn.transactionType === 'PURCHASE') return h.category === 'EXPENSE';
    if (txn.transactionType === 'PAYMENT')  return h.category === 'EXPENSE';
    return true;
  });

  const set = (name, value) => setForm(p => ({ ...p, [name]: value }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        transactionType:  form.transactionType,
        transactionDate:  form.transactionDate,
        amount:           String(form.amount),
        taxAmount:        form.taxAmount   ? String(form.taxAmount) : null,
        paymentMode:      form.paymentMode || null,
        referenceNo:      form.referenceNo || null,
        dueDate:          form.dueDate     || null,
        notes:            form.notes       || null,
        branchId:         form.branchId    ? parseInt(form.branchId)    : null,
        projectId:        form.projectId   ? parseInt(form.projectId)   : null,
        accountHeadId:    form.accountHeadId ? parseInt(form.accountHeadId) : null,
        customerId:       form.customerId  ? parseInt(form.customerId)  : null,
        vendorId:         form.payeeType === 'VENDOR'   && form.vendorId   ? parseInt(form.vendorId)   : null,
        employeeId:       form.payeeType === 'EMPLOYEE' && form.employeeId ? parseInt(form.employeeId) : null,
        payeeName:        form.payeeType === 'OTHER'    ? form.payeeName   : null,
      };
      await axios.put(`/financial-transactions/${txn.id}`, payload);
      toast.success('Transaction updated successfully');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="edit-modal">
        <div className="edit-modal-header">
          <h2>Edit Transaction</h2>
          <span className="edit-modal-voucher">{txn.voucherNo}</span>
          <span className={`badge ${TYPE_CLASS[txn.transactionType] || ''}`}>{TYPE_LABELS[txn.transactionType]}</span>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSave} className="edit-modal-body">
          <div className="edit-modal-grid">

            {/* Date */}
            <div className="form-group">
              <label>Date *</label>
              <input type="date" value={form.transactionDate} onChange={e => set('transactionDate', e.target.value)} required />
            </div>

            {/* Amount */}
            <div className="form-group">
              <label>Amount *</label>
              <input type="number" value={form.amount} onChange={e => set('amount', e.target.value)}
                step="0.01" min="0.01" required />
            </div>

            {/* Payment mode (where applicable) */}
            {(txn.transactionType === 'INCOME' || txn.transactionType === 'PAYMENT') && (
              <div className="form-group">
                <label>Payment Mode</label>
                <select value={form.paymentMode} onChange={e => set('paymentMode', e.target.value)}>
                  <option value="">— Select —</option>
                  <option value="CASH">Cash</option>
                  <option value="BANK">Bank Transfer</option>
                  <option value="UPI">UPI</option>
                  <option value="CHEQUE">Cheque</option>
                </select>
              </div>
            )}

            {/* Reference */}
            <div className="form-group">
              <label>{txn.transactionType === 'PURCHASE' ? 'Bill No' : 'Reference No'}</label>
              <input type="text" value={form.referenceNo} onChange={e => set('referenceNo', e.target.value)} />
            </div>

            {/* Due date for purchases */}
            {txn.transactionType === 'PURCHASE' && (
              <div className="form-group">
                <label>Due Date</label>
                <input type="date" value={form.dueDate} onChange={e => set('dueDate', e.target.value)} />
              </div>
            )}

            {/* Tax amount for purchases */}
            {txn.transactionType === 'PURCHASE' && (
              <div className="form-group">
                <label>Tax Amount</label>
                <input type="number" value={form.taxAmount} onChange={e => set('taxAmount', e.target.value)} step="0.01" min="0" />
              </div>
            )}

            {/* Customer (INCOME) */}
            {txn.transactionType === 'INCOME' && (
              <div className="form-group">
                <label>Customer</label>
                <select value={form.customerId} onChange={e => set('customerId', e.target.value)}>
                  <option value="">Select Customer</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}

            {/* Vendor (PURCHASE) */}
            {txn.transactionType === 'PURCHASE' && (
              <div className="form-group">
                <label>Vendor</label>
                <select value={form.vendorId} onChange={e => set('vendorId', e.target.value)}>
                  <option value="">Select Vendor</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
            )}

            {/* Payee tabs (PAYMENT) */}
            {txn.transactionType === 'PAYMENT' && (
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Pay To</label>
                <div className="payee-type-tabs">
                  {['VENDOR', 'EMPLOYEE', 'OTHER'].map(t => (
                    <button key={t} type="button"
                      className={`payee-tab ${form.payeeType === t ? 'active' : ''}`}
                      onClick={() => set('payeeType', t)}>
                      {t === 'VENDOR' ? '🏪 Vendor' : t === 'EMPLOYEE' ? '👤 Employee' : '✏️ Other'}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {txn.transactionType === 'PAYMENT' && form.payeeType === 'VENDOR' && (
              <div className="form-group">
                <label>Vendor</label>
                <select value={form.vendorId} onChange={e => set('vendorId', e.target.value)}>
                  <option value="">Select Vendor</option>
                  {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </div>
            )}
            {txn.transactionType === 'PAYMENT' && form.payeeType === 'EMPLOYEE' && (
              <div className="form-group">
                <label>Employee</label>
                <select value={form.employeeId} onChange={e => set('employeeId', e.target.value)}>
                  <option value="">Select Employee</option>
                  {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.userName || emp.name}</option>)}
                </select>
              </div>
            )}
            {txn.transactionType === 'PAYMENT' && form.payeeType === 'OTHER' && (
              <div className="form-group">
                <label>Payee Name</label>
                <input type="text" value={form.payeeName} onChange={e => set('payeeName', e.target.value)} />
              </div>
            )}

            {/* Account head */}
            <div className="form-group">
              <label>Account Head</label>
              <select value={form.accountHeadId} onChange={e => set('accountHeadId', e.target.value)}>
                <option value="">Select Head</option>
                {filteredHeads.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
              </select>
            </div>

            {/* Project */}
            <div className="form-group">
              <label>Project</label>
              <select value={form.projectId} onChange={e => set('projectId', e.target.value)}>
                <option value="">Select Project</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>

            {/* Notes */}
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Notes</label>
              <textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
            </div>
          </div>

          <div className="edit-modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// ── Main Component ────────────────────────────────────────────────────────────
function FinancialTransactions() {
  const navigate = useNavigate();

  const defaultRange = getPresetRange('this_month');
  const [startDate, setStartDate]       = useState(defaultRange.start);
  const [endDate, setEndDate]           = useState(defaultRange.end);
  const [activePreset, setActivePreset] = useState('this_month');

  const [transactions, setTransactions]     = useState([]);
  const [filteredTransactions, setFiltered] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [searchTerm, setSearch] = useState('');
  const [filterType, setType]   = useState('ALL');
  const [editingTxn, setEditingTxn] = useState(null);
  const [historyTxn, setHistoryTxn] = useState(null);
  const [historyList, setHistoryList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (historyTxn) {
      fetchHistory(historyTxn.id);
    }
  }, [historyTxn]);

  const fetchHistory = async (txnId) => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`/financial-transactions/${txnId}/approval-history`);
      setHistoryList(res.data?.data || []);
    } catch (err) {
      setError('Failed to load approval history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const renderStatus = (txn) => {
    const status = txn.status || 'APPROVED';
    let bg = '#e2e8f0', color = '#475569', label = status;
    if (status === 'APPROVED') { bg = '#dcfce7'; color = '#15803d'; label = 'Approved'; }
    else if (status === 'REJECTED') { bg = '#fee2e2'; color = '#b91c1c'; label = 'Rejected'; }
    else if (status === 'PENDING_L1') { bg = '#fef3c7'; color = '#d97706'; label = 'Pending L1'; }
    else if (status === 'PENDING_L2') { bg = '#f3e8ff'; color = '#7e22ce'; label = 'Pending L2'; }
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
        <span 
          style={{ 
            backgroundColor: bg, 
            color: color, 
            padding: '2px 8px', 
            borderRadius: '4px', 
            fontSize: '11px', 
            fontWeight: '600',
            textTransform: 'uppercase',
            display: 'inline-block'
          }}
        >
          {label}
        </span>
        <button
          type="button"
          onClick={() => setHistoryTxn(txn)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '14px' }}
          title="View Approval History"
        >
          ℹ️
        </button>
      </div>
    );
  };

  const renderHistoryModal = () => {
    if (!historyTxn) return null;
    return (
      <div className="modal-overlay" style={{ zIndex: 1200, display: 'flex', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center' }} onClick={() => setHistoryTxn(null)}>
        <div className="modal-content" style={{ maxWidth: '500px', background: 'white', borderRadius: '8px', width: '90%', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} onClick={e => e.stopPropagation()}>
          <div className="modal-header" style={{ padding: '20px', borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 'bold' }}>Approval History - {historyTxn.voucherNo}</h2>
            <button type="button" className="close-button" onClick={() => setHistoryTxn(null)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
          </div>
          <div className="modal-body" style={{ padding: '20px', maxHeight: '400px', overflowY: 'auto' }}>
            {historyLoading ? (
              <div>Loading history...</div>
            ) : historyList.length === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>No history logs found (Bypassed or legacy transaction).</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {historyList.map((h, i) => {
                  let actColor = 'var(--text-main)';
                  if (h.action?.includes('APPROVE')) actColor = 'green';
                  if (h.action?.includes('REJECT')) actColor = 'red';
                  return (
                    <div key={h.id} style={{ display: 'flex', gap: '12px', borderBottom: i < historyList.length - 1 ? '1px solid #eee' : 'none', paddingBottom: '10px' }}>
                      <div style={{ fontSize: '18px' }}>
                        {h.action?.includes('APPROVE') ? '✅' : h.action?.includes('REJECT') ? '❌' : '📤'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: '600', color: actColor }}>{h.action}</span>
                          <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{new Date(h.performedAt).toLocaleString()}</span>
                        </div>
                        <div style={{ fontSize: '13px', marginTop: '2px' }}>
                          By: <strong>{h.performedByName}</strong> ({h.performedByRole})
                        </div>
                        {h.remarks && (
                          <div style={{ fontSize: '13px', background: '#f8f9fa', padding: '6px', borderRadius: '4px', marginTop: '4px', color: '#555', fontStyle: 'italic' }}>
                            Remarks: "{h.remarks}"
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="modal-footer" style={{ padding: '15px 20px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={() => setHistoryTxn(null)}>Close</button>
          </div>
        </div>
      </div>
    );
  };

  const fetchTransactions = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(
        `/financial-transactions/date-range?startDate=${startDate}&endDate=${endDate}`
      );
      const data = response.data.data || response.data || [];
      setTransactions(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch transactions');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  useEffect(() => {
    let filtered = [...transactions];
    if (filterType !== 'ALL') filtered = filtered.filter(t => t.transactionType === filterType);
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(t =>
        (t.voucherNo || '').toLowerCase().includes(q) ||
        (t.customerName || '').toLowerCase().includes(q) ||
        (t.vendorName || '').toLowerCase().includes(q) ||
        (t.projectName || '').toLowerCase().includes(q) ||
        (t.notes || '').toLowerCase().includes(q)
      );
    }
    setFiltered(filtered);
  }, [transactions, filterType, searchTerm]);

  const applyPreset = (key) => {
    const range = getPresetRange(key);
    if (!range) return;
    setActivePreset(key);
    setStartDate(range.start);
    setEndDate(range.end);
  };

  const handleDateChange = (field, value) => {
    setActivePreset('');
    if (field === 'start') setStartDate(value);
    else setEndDate(value);
  };

  const totals = filteredTransactions.reduce((acc, t) => {
    const amt = parseFloat(t.amount || 0);
    if (t.transactionType === 'INCOME')   acc.income   += amt;
    if (t.transactionType === 'EXPENSE')  acc.expense  += amt;
    if (t.transactionType === 'PURCHASE') acc.purchase += amt;
    if (t.transactionType === 'PAYMENT')  acc.payment  += amt;
    return acc;
  }, { income: 0, expense: 0, purchase: 0, payment: 0 });

  const formatCurrency = (n) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(n);

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <div className="financial-transactions-page">

      {editingTxn && (
        <EditModal
          txn={editingTxn}
          onClose={() => setEditingTxn(null)}
          onSaved={() => { setEditingTxn(null); fetchTransactions(); }}
        />
      )}

      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Financial Transactions</h1>
          <p>View and manage all financial transactions</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={() => navigate('/finance/customer-payments')}>+ Receipt</button>
          <button className="btn-primary" onClick={() => navigate('/finance/vendor-purchases')}>+ Vendor Purchase</button>
          <button className="btn-primary" onClick={() => navigate('/finance/vendor-payments')}>+ Payment</button>
        </div>
      </div>

      {/* Date preset chips */}
      <div className="date-presets">
        {PRESETS.map(p => (
          <button key={p.key}
            className={`preset-chip ${activePreset === p.key ? 'active' : ''}`}
            onClick={() => applyPreset(p.key)}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>From</label>
          <input type="date" value={startDate} onChange={e => handleDateChange('start', e.target.value)} />
        </div>
        <div className="filter-group">
          <label>To</label>
          <input type="date" value={endDate} onChange={e => handleDateChange('end', e.target.value)} />
        </div>
        <div className="filter-group">
          <label>Type</label>
          <select value={filterType} onChange={e => setType(e.target.value)}>
            <option value="ALL">All Types</option>
            <option value="INCOME">Income</option>
            <option value="EXPENSE">Expense</option>
            <option value="PURCHASE">Purchase</option>
            <option value="PAYMENT">Payment</option>
            <option value="ADVANCE">Advance</option>
          </select>
        </div>
        <div className="search-box">
          <input type="text" placeholder="Search voucher, party, project…"
            value={searchTerm} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {/* Summary cards */}
      <div className="summary-cards">
        <div className="summary-card income">
          <div className="summary-label">Income</div>
          <div className="summary-value">{formatCurrency(totals.income)}</div>
        </div>
        <div className="summary-card expense">
          <div className="summary-label">Expense</div>
          <div className="summary-value">{formatCurrency(totals.expense)}</div>
        </div>
        <div className="summary-card purchase">
          <div className="summary-label">Purchase</div>
          <div className="summary-value">{formatCurrency(totals.purchase)}</div>
        </div>
        <div className="summary-card payment">
          <div className="summary-label">Payment</div>
          <div className="summary-value">{formatCurrency(totals.payment)}</div>
        </div>
        <div className="summary-card neutral">
          <div className="summary-label">Transactions</div>
          <div className="summary-value">{filteredTransactions.length}</div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading">Loading transactions…</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Voucher No</th>
                <th>Date</th>
                <th>Type</th>
                <th>Party</th>
                <th>Project</th>
                <th>Account Head</th>
                <th>Mode</th>
                <th>Status</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.length === 0 ? (
                <tr><td colSpan="9" className="no-data">No transactions found for this period</td></tr>
              ) : (
                filteredTransactions.map(t => (
                  <tr key={t.id}>
                    <td className="voucher-no">{t.voucherNo}</td>
                    <td>{formatDate(t.transactionDate)}</td>
                    <td><span className={`badge ${TYPE_CLASS[t.transactionType] || ''}`}>{TYPE_LABELS[t.transactionType] || t.transactionType}</span></td>
                    <td>{t.customerName || t.vendorName || t.employeeName || t.payeeName || '-'}</td>
                    <td>{t.projectName || '-'}</td>
                    <td>{t.accountHeadName || '-'}</td>
                    <td>{MODE_LABELS[t.paymentMode] || t.paymentMode || '-'}</td>
                    <td>{renderStatus(t)}</td>
                    <td className="amount text-right">{formatCurrency(t.amount)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      {renderHistoryModal()}
    </div>
  );
}

export default FinancialTransactions;
